<script setup lang="ts">
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import QuoteSectionSmartCardBenchmarks from '~/components/quote/card/section/QuoteSectionSmartCardBenchmarks.vue'

  const props = defineProps<{
    id?: string | null | undefined
    quote: QuotedPlanVm
    expanded?: boolean
  }>()

  const { getId } = Id.use(props.id ?? props.quote.medicareId)

  const { push } = AppRouter.use()

  const { getQuoteCardEventProps } = QuoteAnalytics.use()
  const { quotingConfig } = QuotingConfig.use()

  const engine = EngineStore.use()
  const { isMystery } = FitScore.use()

  const quoteIndex = computed(() => engine.indexForQuote(props.quote.medicareId))

  const { content, headerBadge, applicablePolicies, policyBadges, policyTable, hasPartBGiveback } =
    await SmartCardBadges.use(() => props.quote, quoteIndex)

  const { coverageItems, onUpdateNeeds } = SmartCardCoverage.use(() => props.quote, content, {
    doctors: () => getId.value('doctors-sidebar'),
    drugCosts: () => getId.value('drug-monthly-card-breakdown-sidebar'),
    healthcareCosts: () => getId.value('healthcare-costs-sidebar')
  })

  const onPlanDetails = () => {
    push(`/details/${props.quote.medicareId}`)
  }
</script>

<template>
  <article
    :id="getId('smart-card')"
    class="smart-card-shadow flex h-full w-full flex-col rounded-quote border border-border bg-white"
  >
    <header class="flex w-full flex-col items-center justify-between gap-y-2 px-7 py-4 md:flex-row">
      <div class="flex flex-col items-center gap-y-1 md:items-start">
        <QuoteBadge class="mb-2" small :variant="headerBadge.color" v-if="headerBadge">
          {{ headerBadge.text }}
        </QuoteBadge>
        <PxCarrierLogo :logoKey="quote.details.logoKey!" :plan-year="quote.details.year!" />

        <span class="text-center text-xl font-bold leading-tight md:text-left">{{
          quote.name
        }}</span>
        <div class="flex flex-col gap-y-2 md:-ml-2 md:flex-row md:items-center md:gap-x-1">
          <div class="flex items-center justify-center gap-x-2 md:contents">
            <PxStarRating
              star-class="text-[#ffc107] scale-[65%]"
              :starRating="quote.details.starRating!"
            />
            <QuoteBadge small>{{ quote.details.year }}</QuoteBadge>
          </div>
          <div
            class="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 md:contents md:justify-start"
          >
            <QuoteBadge v-if="hasPartBGiveback" variant="green" small title-case>
              Part B Giveback
            </QuoteBadge>
            <QuoteBadge
              title-case
              small
              :variant="variant"
              v-for="{ tag, variant } in policyBadges"
            >
              {{ tag }}
            </QuoteBadge>
          </div>
        </div>
      </div>
      <div class="hidden flex-row items-center gap-x-2 gap-y-3 md:flex md:flex-col">
        <HpButton
          :variant="quotingConfig.smartCardPrimaryCta === 'apply' ? 'minimal' : 'primary'"
          @click="onPlanDetails"
          >See Plan Details</HpButton
        >
        <PxApplyButton
          :link="quotingConfig.smartCardPrimaryCta === 'details'"
          :medicareId="quote.medicareId"
          :externalAppUrl="quote.details.externalAppUrl"
          :carrierName="quote.details.carrierName!"
          :planYear="quote.details.year!"
          :type="quote.type"
          :event-data="getQuoteCardEventProps('apply', quote)"
        >
          Select This Plan
        </PxApplyButton>
      </div>
    </header>
    <div class="mb-4 flex flex-col gap-y-6">
      <div
        :class="[
          'mx-4 flex flex-col items-center gap-x-6 gap-y-4 self-end rounded-quote bg-[#F5F5F5] px-6 py-4 md:mx-0 md:min-h-28 md:w-[96%] md:flex-row md:gap-y-0 md:rounded-l-full md:rounded-r-none md:py-2 md:pl-8 md:pr-4',
          {
            '!bg-[#363B79] text-white': quoteIndex === 0
          }
        ]"
      >
        <QuotePlanScoreV2
          inset-label
          label="FIT"
          solid-dial
          size="extra-large"
          :score="quote.score"
          :mystery="isMystery(quote)"
          :track-color="quoteIndex === 0 ? '#363B79' : '#E9E9E9'"
        />
        <div class="flex flex-col">
          <div
            :class="[
              'w-full text-center text-xs tracking-wider text-gray-700 md:w-auto md:text-left',
              { '!text-gray-400': quoteIndex === 0 }
            ]"
          >
            AI GENERATED SUMMARY
          </div>
          <div class="text-center text-xl font-semibold leading-tight md:text-left">
            {{ quote.summary || content.data.summary_fallback }}
            <span v-if="engine.isEmptyState">
              <HpAnchor
                :class="[
                  'text-xl font-semibold text-primary underline-offset-4',
                  { '!text-white': quoteIndex === 0 }
                ]"
                @click.prevent="onUpdateNeeds"
                >{{ content.data.empty_state_summary_link }}</HpAnchor
              >
            </span>
          </div>
        </div>
      </div>
      <QuoteSectionSmartCardBenchmarks
        :coverage-items="coverageItems"
        :is-empty-state="engine.isEmptyState"
      />
      <HlDisclosure :default-open="expanded" :id="`${quote.medicareId}-details`" v-slot="{ open }">
        <div class="w-full items-center justify-center px-2 lg:px-16">
          <HlDisclosureButton as="div" class="flex w-full items-center justify-center gap-x-2">
            <div class="font-semibold text-primary no-underline">
              {{ content.data.expansion_text }}
            </div>
            <HpIcon
              name="chevron-down-small"
              :class="[
                'scale-75 text-primary transition-transform duration-500',
                { 'rotate-180': open }
              ]"
            />
          </HlDisclosureButton>
          <TransitionSlide
            appear
            :mode="'out-in'"
            :enter="{
              from: {
                y: -20
              },
              duration: 0.3
            }"
            :leave="{
              to: {
                y: -20
              },
              duration: 0.1
            }"
          >
            <HlDisclosurePanel class="flex w-full flex-col gap-y-4 py-4">
              <QuoteDetailPanel v-if="applicablePolicies.length">
                <template #title>PLAN TYPE</template>
                <div class="flex w-full flex-col gap-y-2">
                  <div class="flex items-baseline gap-x-4" v-for="policy in policyTable">
                    <div class="w-20 shrink-0">{{ policy.name }}</div>
                    <div class="leading-tight">
                      <span
                        :class="[
                          'font-bold',
                          { 'text-success-dark': policy.variant === 'green' },
                          { 'text-gray-700': policy.variant === 'darkGray' }
                        ]"
                        >{{ policy.summary }}</span
                      >&nbsp;{{ policy.description }}
                    </div>
                  </div>
                </div>
              </QuoteDetailPanel>
              <QuoteDetailPanel>
                <template #title>PLAN COST SHARING</template>
                <div
                  class="flex w-full flex-col justify-between divide-y divide-border md:flex-row md:divide-y-0"
                >
                  <div class="flex flex-col divide-y divide-border md:w-[45%]">
                    <div class="flex w-full justify-between py-2">
                      <div>Primary care visit copay</div>
                      <div class="font-bold">
                        {{ formatCopay(quote.details.providerBenefits?.doctorCopay) }}
                      </div>
                    </div>
                    <div class="flex w-full justify-between py-2">
                      <div>Specialist copay</div>
                      <div class="font-bold">
                        {{ formatCopay(quote.details.providerBenefits?.specialistCopay) }}
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col divide-y divide-border md:w-[40%]">
                    <div class="flex w-full justify-between py-2">
                      <div>Medical deductible</div>
                      <div class="font-bold">
                        {{ formatUsd(quote.details.medicalDeductible) }}
                      </div>
                    </div>
                    <div class="flex w-full justify-between py-2">
                      <div>Max out-of-pocket</div>
                      <div class="font-bold">
                        {{ formatUsd(quote.details.maxOutOfPocket) }}
                      </div>
                    </div>
                  </div>
                </div>
              </QuoteDetailPanel>
              <QuoteDetailPanel>
                <template #title>EXTRA BENEFITS</template>
                <div class="flex w-full justify-center">
                  <QuoteCardSectionBenefitHighlights
                    minimal
                    stacked
                    :quote="quote"
                    :id="`${quote.medicareId}-quick-view`"
                  />
                </div>
              </QuoteDetailPanel>
              <HpAnchor
                @click.prevent="onPlanDetails"
                class="hidden px-2 font-semibold no-underline md:flex"
              >
                See the full plan details, costs, and coverage
                <HpIcon class="scale-50" name="chevron-right" />
              </HpAnchor>
            </HlDisclosurePanel>
          </TransitionSlide>
        </div>
      </HlDisclosure>
    </div>
    <div class="mt-4 flex-row items-center gap-x-2 gap-y-2 px-4 md:hidden">
      <HpButton
        class="mb-4"
        :variant="quotingConfig.smartCardPrimaryCta === 'apply' ? 'minimal' : 'primary'"
        @click="onPlanDetails"
        >See Plan Details</HpButton
      >
      <PxApplyButton
        class="mb-4"
        :link="quotingConfig.smartCardPrimaryCta === 'details'"
        :medicareId="quote.medicareId"
        :externalAppUrl="quote.details.externalAppUrl"
        :carrierName="quote.details.carrierName!"
        :planYear="quote.details.year!"
        :type="quote.type"
        :event-data="getQuoteCardEventProps('apply', quote)"
      >
        Select This Plan
      </PxApplyButton>
    </div>
    <footer
      class="flex w-full flex-col items-center justify-between gap-y-4 border-t border-t-border px-8 py-3 md:h-14 md:flex-row md:gap-y-0"
    >
      <div class="flex items-center gap-x-4">
        <PxComparePlanControl
          :medicare-id="quote.medicareId"
          :event-data="getQuoteCardEventProps('add-to-compare', quote)"
          muted
        />
        <PxSavedPlanControl
          :medicare-id="quote.medicareId"
          :year="quote.details.year!"
          :event-data="getQuoteCardEventProps('save-this-plan', quote)"
          muted
        />
      </div>
      <div class="flex cursor-pointer items-center gap-x-1" @click="onUpdateNeeds">
        <HpIcon name="edit-circle" class="w-fit" />
        <span class="font-semibold text-primary xs:text-sm xs:font-semibold xs:text-gray-800"
          >Update Your Information</span
        >
      </div>
    </footer>
    <QuoteDoctorNetworkSidebar :id="getId('doctors-sidebar')" :quote="quote" />
    <DetailsDrugMonthlyBreakdownSidebarV2
      :id="getId('drug-monthly-card-breakdown-sidebar')"
      :quote="quote"
    />
    <QuoteHealthcareCostsSidebar :id="getId('healthcare-costs-sidebar')" :quote="quote" />
  </article>
</template>

<style scoped>
  .smart-card-shadow {
    box-shadow: 0px 4px 12px -2px rgba(111, 114, 133, 0.2);
  }

  :deep(.plan-logo-long) {
    @apply h-fit !max-w-60;
  }

  :deep(.plan-logo-short) {
    @apply !max-h-12 w-fit;
  }
</style>
